/**
 * Score → color bands for the numeric sentiment payloads (`sentiment_scatter`,
 * `sentiment_histogram`), where the API sends a continuous average score rather
 * than a named category.
 *
 * Bands rather than a continuous ramp: mid-ramp colors are indistinguishable
 * once there are hundreds of marks on screen, and bands give a real legend that
 * doubles as a filter (isolate the negative calls with one click).
 *
 * Colors come from SENTIMENT_LEVELS (src/components/widgets/sentimentScale.ts)
 * so a sentiment color means the same thing here as it does on
 * SentimentSplitWidget and DonutPieWidget — that file also documents the
 * explicit product decision to use green→red and its colorblind caveat.
 */

import {
  SENTIMENT_LEVELS,
  type SentimentLevelKey,
} from "src/components/widgets/sentimentScale";

const levelColor = (key: SentimentLevelKey, fallback: string): string =>
  SENTIMENT_LEVELS.find((level) => level.key === key)?.color ?? fallback;

export type ScoreBand = {
  /** Lowest score in this band; it runs up to the next band's `min`. */
  min: number;
  label: string;
  color: string;
};

/**
 * Ordered positive → negative, which is both the legend reading order and the
 * lookup order (first band whose `min` a score reaches wins), so boundaries are
 * inclusive at the bottom: 0.8 is Positive, 0.6 is Mostly positive.
 *
 * "Mixed" from SENTIMENT_LEVELS is deliberately absent — it isn't a point on
 * the positive→negative line, so a 0..1 average can never land there.
 */
export const SCORE_BANDS: ScoreBand[] = [
  { min: 0.8, label: "Positive", color: levelColor("positive", "#15803D") },
  {
    min: 0.6,
    label: "Mostly positive",
    color: levelColor("mostly_positive", "#4ADE80"),
  },
  { min: 0.4, label: "Neutral", color: levelColor("neutral", "#64748B") },
  {
    min: 0.2,
    label: "Mostly negative",
    color: levelColor("mostly_negative", "#F87171"),
  },
  { min: 0, label: "Negative", color: levelColor("negative", "#DC2626") },
];

/**
 * For marks with no score at all — the histogram sends `score: null` for an
 * empty bucket. They get their own muted band instead of being silently colored
 * as if they were neutral.
 */
export const UNSCORED_BAND: ScoreBand = {
  min: Number.NEGATIVE_INFINITY,
  label: "No sentiment",
  color: "#CBD5E1",
};

/** Every band in legend order, unscored last. */
export const ALL_BANDS: ScoreBand[] = [...SCORE_BANDS, UNSCORED_BAND];

export const bandFor = (score: number | null | undefined): ScoreBand => {
  if (typeof score !== "number" || !Number.isFinite(score)) return UNSCORED_BAND;
  return SCORE_BANDS.find((band) => score >= band.min) ?? UNSCORED_BAND;
};

/**
 * The backend's own wording for a band, taken only when every mark in that band
 * unanimously reports the same `sentiment_label` ("Positive lean", "Neutral /
 * Mixed", …); otherwise our own band label.
 *
 * The unanimity requirement is the point: a disagreement means the backend's
 * cut-offs and SCORE_BANDS above have drifted apart, and quietly picking one of
 * the names would hide that. Falling back to our label keeps the legend honest
 * about what the color actually represents.
 */
export const bandDisplayName = (
  band: ScoreBand,
  marks: Array<{ sentiment_label?: string | null }>,
): string => {
  const supplied = marks
    .map((m) => m.sentiment_label)
    .filter(
      (label): label is string =>
        typeof label === "string" && label.trim().length > 0,
    )
    .map((label) => label.trim());

  if (supplied.length === 0 || supplied.length !== marks.length) {
    return band.label;
  }
  return new Set(supplied).size === 1 ? (supplied[0] ?? band.label) : band.label;
};

/**
 * 0.525 → "0.53", 1 → "1", 0.6 → "0.6". Trailing zeros are dropped so the
 * common one-decimal scores don't read as false precision.
 */
export const formatScore = (score: number): string =>
  String(Number(score.toFixed(2)));

/** Fade a band color for fills that overlap (used by the scatter's dots). */
export const fadeBandColor = (hex: string, alpha: number): string => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => `${c}${c}`)
          .join("")
      : normalized;
  if (expanded.length !== 6) return hex;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
};
